import {
  Injectable,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import {
  Database,
  ref,
  query,
  limitToLast,
  onChildAdded,
} from "@angular/fire/database";
import { BvToastService } from "../design-system";
import { TournamentService } from "./tournament.service";
import { ProfileService } from "./profile.service";
import { TranslateService } from "@ngx-translate/core";

// Turns NEW tournament-log entries into toasts for the person on the
// tournament screen (you were registered / seated / eliminated).
//
// It only ever cared about entries written after the screen opened, yet it
// used to attach a plain child_added listener to the whole log, which made
// every tournament screen (players included) download the entire log, up to
// ~200 KB, and then throw it away. A limitToLast(1) window delivers the newest
// existing entry once (skipped by the startTime check) and then each new push
// as it enters the window. Push keys are time-ordered, so a new entry is always
// the last one.
@Injectable()
export class LogProcessService {
  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  startTime!: number;
  private unsubscribe: (() => void) | null = null;
  constructor(
    private snackBar: BvToastService,
    private tournServ: TournamentService,
    private profServ: ProfileService,
    private translate: TranslateService
  ) {}
  init(tournID: string) {
    this.startTime = Date.now();
    this.destroy();
    runInInjectionContext(this.injector, () => {
      const newest = query(
        ref(this.mdb, "logs/tournaments/" + tournID),
        limitToLast(1)
      );
      this.unsubscribe = onChildAdded(newest, (snapshot) => {
        this.processLog(snapshot.val());
      });
    });
  }
  destroy() {
    if (this.unsubscribe) this.unsubscribe();
    this.unsubscribe = null;
  }
  processLog(entry: any) {
    if (!entry || entry.timestamp < this.startTime) return;
    if (entry.type == "ELIMINATION")
      setTimeout(() => {
        this.processElimination(entry);
      });

    if (entry.type == "DRAW_SEATS") {
      setTimeout(() => {
        this.processSeatDraw(entry);
      });
    }
    if (entry.type == "REGISTER" && !this.tournServ.latestLive?.seated) {
      this.translate.get(["PLAYER_REGISTERED"]).subscribe((res: any) => {
        if (entry && entry.undoParams) {
          this.snackBar.open(
            res["PLAYER_REGISTERED"] + ": " + entry.undoParams.name,
            undefined,
            {
              duration: 3000,
            }
          );
        }
      });
    }
  }
  processElimination(entry: any) {
    if (
      this.tournServ.latestTourn &&
      this.tournServ.latestTourn.params.manageRegistrations
    ) {
      let uid = this.profServ.uid;
      if (
        this.tournServ.latestTourn &&
        this.tournServ.latestTourn.players &&
        entry.undoParams &&
        entry.undoParams.uid == uid &&
        this.tournServ.latestTourn.players[uid!]
      ) {
        let finish = this.tournServ.latestTourn.players[uid!].finish;
        this.translate
          .get(["ELIMINATED_POSITION", "CLOSE"])
          .subscribe((res: any) => {
            this.snackBar.open(
              res["ELIMINATED_POSITION"] + ": " + finish,
              res["CLOSE"]
            );
          });
      }
      // Leaderboard and elimination info boxes are handled by InfoBoxService
      // from tournament state and elimination timestamps in player data.
    }
  }
  processSeatDraw(entry: any) {
    if (
      this.tournServ.latestTourn &&
      this.tournServ.latestTourn.players &&
      this.tournServ.latestTourn.players[this.profServ.uid!] &&
      !this.tournServ.latestTourn.players[this.profServ.uid!].finish
    )
      this.translate
        .get(["BEEN_SEATED", "CLOSE", "SEAT", "TABLE"])
        .subscribe((res: any) => {
          const myTable = this.tournServ.tableNumberDisplay(
            Number(this.tournServ.latestTourn.players[this.profServ.uid!].table),
          );
          let message =
            res["BEEN_SEATED"] +
            " (" +
            res["TABLE"] +
            ":" +
            myTable +
            " " +
            res["SEAT"] +
            ":" +
            this.tournServ.latestTourn.players[this.profServ.uid!].seat +
            ")";
          this.snackBar.open(message, res["CLOSE"]);
        });
  }
}
